import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../button';
import { Dialog, DialogTitle, DialogBody } from '../dialog';
import { useUser } from '../UserContext';
import { useClients, useContracts, useOrdenesTrabajoRaw } from '../../contexts/DataContext';
import { useOrgMiembros } from '../../hooks/useOrgMiembros';
import { useOtAcciones, useOtConfig } from '../../hooks/useOrdenesTrabajo';
import { PanelAsignacion } from './OtDialog';
import {
  MOTIVO_MAX_LEN, folioTexto, fasesDe, faseDe, equipoDe, cargaPorPersona, isAbierta, normalizarAsignados, destinatariosAprobacion,
} from '../../utils/ordenesTrabajo';

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

/**
 * The relevo: a coordinador passes an OT to another fase and names who works
 * it there. The fase's equipo (from config) groups the people offered; the
 * load bar and the "Ejecutivo del fiado" tag are the same as at creation.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Object|null} props.ot  - decorated row
 * @param {string|null} [props.faseDestinoId]  - preselected fase (default: the next one)
 * @param {Function} [props.onDone]
 */
export function OtPasarFaseDialog({ isOpen, onClose, ot, faseDestinoId = null, onDone }) {
  const { user } = useUser();
  const { clientsById } = useClients();
  const { contractsList } = useContracts();
  const { ordenesTrabajo } = useOrdenesTrabajoRaw();
  const { miembros } = useOrgMiembros();
  const { config } = useOtConfig();
  const { moverFase, asignar } = useOtAcciones();

  const fases = useMemo(() => fasesDe(config, ot?.tipo), [config, ot?.tipo]);
  const idxActual = fases.findIndex((f) => f.id === ot?.faseId);
  const siguiente = idxActual >= 0 ? fases[idxActual + 1] : fases[0];

  const [faseId, setFaseId] = useState('');
  const [asignados, setAsignados] = useState([]);
  const [nota, setNota] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFaseId(faseDestinoId || siguiente?.id || '');
    setAsignados([]);
    setNota('');
    setError(null);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ot?.id, faseDestinoId]);

  const destino = useMemo(() => faseDe(fases, faseId), [fases, faseId]);
  const equipoDestino = destino?.equipoId ? equipoDe(config.equipos, destino.equipoId) : null;

  // Load WITHOUT this OT: whoever carries it now is about to hand it over,
  // so it must not count against them (Fran, 2026-10-19: "se debe liberar la
  // carga del responsable anterior").
  const carga = useMemo(() => {
    const abiertas = (ordenesTrabajo || []).filter((o) => isAbierta(o) && o.id !== ot?.id);
    const out = {};
    cargaPorPersona(abiertas, miembros).forEach((p) => { out[p.uid] = p.total; });
    return out;
  }, [ordenesTrabajo, miembros, ot?.id]);

  const ejecutivos = useMemo(() => {
    const set = new Set();
    (clientsById[ot?.clienteId]?.assignedUsers || []).forEach((u) => set.add(u));
    (contractsList.find((c) => c.id === ot?.contratoId)?.assignedUsers || []).forEach((u) => set.add(u));
    return set;
  }, [clientsById, contractsList, ot?.clienteId, ot?.contratoId]);

  const toggle = (uid) => setAsignados((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));

  const confirmar = async () => {
    if (!destino) { setError('Elige la fase.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      // Same fase = staffing it (an OT that came out of an approval with
      // nobody on it), not a move: the fase clock must not restart.
      if (destino.id === ot.faseId) await asignar(ot, asignados);
      else await moverFase(ot, destino.id, { asignados: destino.requiereAprobacion ? [] : asignados, nota });
      onDone?.();
      onClose();
    } catch (e) {
      console.error('relevo failed', e);
      setError(e.message || 'No se pudo pasar la orden.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !ot) return null;

  const actuales = normalizarAsignados(ot);
  const nombreDe = (uid) => miembros.find((m) => m.id === uid)?.nombre || uid;

  return (
    <Dialog open onClose={submitting ? () => {} : onClose} size="lg">
      <DialogTitle>
        <span className="text-azul-surelia">{destino?.id === ot.faseId ? 'Asignar' : 'Avanzar'} {folioTexto(ot.folio)} · {ot.titulo}</span>
      </DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          {ot.faseTerminada?.at && (
            <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {ot.faseNombre || 'Fase'} terminada{ot.faseTerminada.nombre ? ` por ${ot.faseTerminada.nombre}` : ''}
              {ot.faseTerminada.nota ? `. ${ot.faseTerminada.nota}` : ''}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{destino?.id === ot.faseId ? 'Fase' : 'Avanzar a'}</label>
              <select value={faseId} onChange={(e) => setFaseId(e.target.value)} className={field}>
                {fases.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nombre}{f.id === ot.faseId ? ' (actual)' : ''}{f.diasObjetivo ? ` · ${f.diasObjetivo} d` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Equipo de la fase</label>
              <div className="py-2 text-sm text-zinc-700">{equipoDestino?.nombre || <span className="text-zinc-400">Sin equipo configurado</span>}</div>
            </div>
          </div>

          {destino?.requiereAprobacion ? (
            // An approval fase has no asignados: the aprobadores act on it.
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {destino.nombre} es una fase de aprobación. La resuelven: {destinatariosAprobacion(config).map(nombreDe).join(', ') || 'el owner'}.
            </div>
          ) : (
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600">Quién la trabaja en {destino?.nombre || 'la fase'}</label>
              {actuales.length > 0 && (
                <span className="text-[11px] text-zinc-400">Hasta ahora: {actuales.map(nombreDe).join(', ')}</span>
              )}
            </div>
            <PanelAsignacion
              miembros={miembros}
              equipos={config.equipos}
              equiposElegidos={equipoDestino ? [equipoDestino.id] : []}
              carga={carga}
              ejecutivos={ejecutivos}
              asignados={asignados}
              meUid={user?.uid}
              onToggle={toggle}
            />
          </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nota (opcional)</label>
            <input
              type="text"
              value={nota}
              maxLength={MOTIVO_MAX_LEN}
              onChange={(e) => setNota(e.target.value)}
              className={field}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button plain onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button color="surelia" onClick={confirmar} disabled={submitting || !destino}>
              {destino?.requiereAprobacion ? `Avanzar a ${destino.nombre}`
                : destino?.id === ot.faseId ? (asignados.length ? `Asignar a ${asignados.length}` : 'Dejar sin responsable')
                  : asignados.length ? `Avanzar y asignar a ${asignados.length}` : 'Avanzar sin asignar'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default OtPasarFaseDialog;
